import { showDialog } from '@/utils/dialogs';
/**
 * AnalysisModal
 *
 * Modal for configuring transcript analysis options:
 * - Analysis mode (ICF / Custom) with tier gating
 * - ICF level selection (Auto / ACC / PCC / MCC)
 * - Marker table toggle
 * - Language selection
 * - ICF progress tracking toggle
 */

import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    ScrollView,
    ActivityIndicator,
    StyleSheet,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useWaitingTips } from '../../hooks/useWaitingTips';
import { useAuth } from '../../context/AuthContext';
import { AnalysisOptionsForm } from './AnalysisOptionsForm';
import type { EngagementPickerOption } from './AnalysisOptionsForm';
import { ICF_FORMS } from '../../data/icfFormStatements';
import { MentorEngagementsAPI } from '../../services/api/mentorEngagementsAPI';
import UpgradeModal from '../billing/UpgradeModal';
import { getAnalysisTierAccess } from '../../utils/planContext';
import type { AnalysisOptions, AnalysisMode, ICFLevel } from '../../types';
import { creditsAPI } from '../../services/api';
import { loadAnalysisPreferences, saveAnalysisPreferences } from '../../services/analysisPreferences';
import { buildAnalysisSummary } from './analysisSummary';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { chatSendErrorMessage } from '../../screens/chat/chatErrors';
import { reviewDesk } from '../../design-system';

interface AnalysisModalProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (options: AnalysisOptions) => Promise<void>;
    transcriptName?: string;
    loading?: boolean;
    /**
     * Opens the modal already attached to a mentor engagement (WS-5.2 "Upload
     * session"). The level is pinned to the engagement's target level and the
     * ICF-progress toggle is hidden and forced off.
     */
    engagementPreset?: EngagementPickerOption | null;
}

export function AnalysisModal({
    visible,
    onClose,
    onSubmit,
    transcriptName,
    loading = false,
    engagementPreset = null,
}: AnalysisModalProps) {
    const { t, language: currentLanguage } = useTranslation();
    const { billingStatus } = useAuth();

    // Tier access
    const tierAccess = getAnalysisTierAccess(billingStatus?.plan_tier);
    const isTier3 = billingStatus?.plan_tier === 'tier3';

    // Options state
    const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('icf');
    const [requestedLevel, setRequestedLevel] = useState<ICFLevel>('auto');
    const [includeMarkerTable, setIncludeMarkerTable] = useState(false);
    const [customPrompt, setCustomPrompt] = useState('');
    const [language, setLanguage] = useState<'tr' | 'en'>(currentLanguage === 'en' ? 'en' : 'tr');
    const [includeInIcfProgress, setIncludeInIcfProgress] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [remainingCredits, setRemainingCredits] = useState<number | null>(null);
    const [engagementOptions, setEngagementOptions] = useState<EngagementPickerOption[]>([]);
    const [selectedEngagementId, setSelectedEngagementId] = useState<number | null>(null);

    const selectedEngagement = engagementPreset
        ? engagementPreset
        : engagementOptions.find((item) => item.id === selectedEngagementId) || null;

    const mode = selectedEngagement ? 'engagement' : 'coach';
    const summary = buildAnalysisSummary({ mode, analysisMode, requestedLevel, engagementLevel: selectedEngagement?.target_level, includeMarkerTable, includeInIcfProgress: tierAccess.allowLevelSelect && includeInIcfProgress, language });
    const engagementVars = { mentee: selectedEngagement?.mentee_name, level: selectedEngagement?.target_level.toUpperCase(), sof_revision: selectedEngagement?.sof_revision };

    useEffect(() => {
        if (!visible) return;
        let active = true;
        setRemainingCredits(null);
        void creditsAPI.getStatus().then(status => {
            if (active && typeof status.total_available === 'number' && !status.usage_hidden && !status.credit_exempt) setRemainingCredits(status.total_available);
        }).catch(() => {});
        return () => { active = false; };
    }, [visible]);

    const presetId = engagementPreset?.id;
    const presetLevel = engagementPreset?.target_level;

    // Only Tier 3 mentors own engagements — never probe the endpoint for others.
    useEffect(() => {
        if (!visible || !isTier3 || presetId != null) return;
        let active = true;
        MentorEngagementsAPI.list('active')
            .then((items) => {
                if (!active) return;
                setEngagementOptions(items.map((item) => ({
                    id: item.id,
                    mentee_name: item.mentee_name,
                    target_level: item.target_level,
                    sof_revision: ICF_FORMS[item.target_level].sof_revision,
                })));
            })
            .catch(() => {
                // A mentor with no engagement access simply gets no picker.
                if (active) setEngagementOptions([]);
            });
        return () => { active = false; };
    }, [visible, isTier3, presetId]);

    // In-app upgrade paywall (opened from a locked analysis feature).
    const [upgrade, setUpgrade] = useState<{ visible: boolean; requiredTier: 'tier2' | 'tier3'; featureKey: string }>({
        visible: false,
        requiredTier: 'tier2',
        featureKey: '',
    });
    const handleUpgradePress = (requiredTier: 'tier2' | 'tier3', featureKey: string) =>
        setUpgrade({ visible: true, requiredTier, featureKey });
    const upgradeModalEl = (
        <UpgradeModal
            visible={upgrade.visible}
            requiredTier={upgrade.requiredTier}
            featureKey={upgrade.featureKey}
            onClose={() => setUpgrade((s) => ({ ...s, visible: false }))}
        />
    );

    // Compute loading state
    const isLoading = loading || submitting;

    // Rotating tips for the progress overlay
    const waitingTipKey = useWaitingTips(isLoading);

    // Reset form when modal opens
    useEffect(() => {
        let active = true;
        if (visible) {
            void loadAnalysisPreferences().then(prefs => {
                if (!active) return;
                if (prefs.language) setLanguage(prefs.language);
                if (presetId == null) {
                    setRequestedLevel(tierAccess.allowLevelSelect ? prefs.requestedLevel || 'auto' : 'auto');
                    setIncludeMarkerTable(tierAccess.allowMarkerTable && !!prefs.includeMarkerTable);
                }
            });
            setAnalysisMode('icf');
            setRequestedLevel(presetLevel || 'auto');
            setIncludeMarkerTable(false);
            setCustomPrompt('');
            setLanguage(currentLanguage === 'en' ? 'en' : 'tr');
            setIncludeInIcfProgress(presetId != null ? false : tierAccess.allowLevelSelect);
            setSelectedEngagementId(presetId ?? null);
            setShowAdvanced(false);
        }
        return () => { active = false; };
    // Keyed on primitives: the caller passes an inline object literal, so a
    // parent re-render must not wipe the mentor's in-progress choices.
    }, [visible, currentLanguage, presetId, presetLevel, tierAccess.allowLevelSelect, tierAccess.allowMarkerTable]);

    const handleSubmit = async () => {
        if (!canSubmit()) return;
        setSubmitting(true);
        try {
            const options: AnalysisOptions = {
                analysisMode: selectedEngagement ? 'icf' : analysisMode,
                requestedLevel: selectedEngagement || analysisMode === 'icf'
                    ? (selectedEngagement ? selectedEngagement.target_level : requestedLevel)
                    : undefined,
                includeMarkerTable: selectedEngagement ? true : analysisMode === 'icf' ? includeMarkerTable : undefined,
                customPrompt: selectedEngagement || analysisMode === 'custom' ? customPrompt.trim() || undefined : undefined,
                // Mentee sessions must never enter the mentor's own ICF Progress (D17/§3).
                includeInIcfProgress: selectedEngagement
                    ? false
                    : tierAccess.allowLevelSelect && includeInIcfProgress,
                language,
                engagementId: selectedEngagement ? selectedEngagement.id : undefined,
            };
            await saveAnalysisPreferences({ language, requestedLevel, includeMarkerTable }, mode);
            await onSubmit(options);
            resetAndClose();
        } catch (error: any) {
            console.error('Analysis submission error:', error);
            // Surface the failure instead of swallowing it — a silent catch here
            // makes "Start analysis" look like it does nothing.
            const trialUsed = trialAnalysisUsedMessage(t, currentLanguage, error);
            // A spent trial analysis is not an error: no "Analysis error" prefix.
            // CHAT-19: map to translated text, never raw backend/network text.
            showDialog(trialUsed || `${t('chat.analysisError')}: ${chatSendErrorMessage(t, error, 'chat.analysisFailed')}`);
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = (): boolean => {
        if (isLoading) return false;
        if (!selectedEngagement && analysisMode === 'custom' && !customPrompt.trim()) return false;
        return true;
    };

    const resetAndClose = () => {
        setAnalysisMode('icf');
        setRequestedLevel(engagementPreset ? engagementPreset.target_level : 'auto');
        setIncludeMarkerTable(false);
        setCustomPrompt('');
        setLanguage(currentLanguage === 'en' ? 'en' : 'tr');
        setIncludeInIcfProgress(engagementPreset ? false : tierAccess.allowLevelSelect);
        setSelectedEngagementId(engagementPreset ? engagementPreset.id : null);
        setShowAdvanced(false);
        onClose();
    };

    const handleEngagementChange = (engagementId: number | null) => {
        setSelectedEngagementId(engagementId);
        setShowAdvanced(false);
        const engagement = engagementOptions.find((item) => item.id === engagementId);
        if (engagement) {
            setAnalysisMode('icf');
        }
    };

    const settingsToggle = <TouchableOpacity disabled={isLoading} onPress={() => setShowAdvanced(value => !value)}><Text>{t(showAdvanced ? 'analysis.simple.hideSettings' : selectedEngagement ? 'analysis.modal.additionalOptions' : 'analysis.simple.changeSettings')}</Text></TouchableOpacity>;

      return (
          <>
          <Modal
              visible={visible}
              animationType="fade"
              transparent
              onRequestClose={resetAndClose}
          >
              <View style={simpleStyles.backdrop}>
                  <View style={simpleStyles.sheet}>
                      <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ padding: 22 }}>
                      <View style={simpleStyles.header}>
                          <TouchableOpacity testID="analysis_modal_cancel" accessibilityLabel={t('common.cancel')} onPress={resetAndClose} disabled={isLoading} style={simpleStyles.closeButton}>
                              <MaterialIcons name="close" size={22} color={reviewDesk.inkSoft} />
                          </TouchableOpacity>
                          <View style={{ flex: 1 }}>
                              <Text style={simpleStyles.headerTitle}>
                                  {t(selectedEngagement ? 'analysis.modal.prepareSessionReview' : 'analysis.simple.title')}
                              </Text>
                              <Text style={simpleStyles.headerSubtitle}>
                                  {t(selectedEngagement ? 'analysis.modal.engagementSubtitle' : 'analysis.simple.subtitle', engagementVars)}
                              </Text>
                          </View>
                      </View>

                      {transcriptName && (
                          <View style={simpleStyles.fileCard}>
                              <View style={simpleStyles.fileIcon}>
                                  <MaterialIcons name="description" size={26} color={reviewDesk.accent} />
                              </View>
                              <View style={{ flex: 1 }}>
                                  <Text style={simpleStyles.fileName} numberOfLines={1}>{transcriptName}</Text>
                                  <Text style={simpleStyles.fileMeta}>
                                      {t('analysis.simple.ready')}
                                  </Text>
                              </View>
                              <View style={simpleStyles.readyPill}>
                                  <MaterialIcons name="check-circle" size={14} color={reviewDesk.accent} />
                                  <Text style={simpleStyles.readyPillText}>{t('analysis.simple.ready')}</Text>
                              </View>
                          </View>
                      )}

                      {mode === 'coach' && <View style={simpleStyles.defaultsCard}>
                          <Text style={simpleStyles.sectionTitle}>{t(summary.title)}</Text>
                          {summary.rows.map(row => <Text key={row.key} style={simpleStyles.defaultText}>
                              {t(row.label)}: {row.value.includes('.') ? t(row.value) : row.value}
                          </Text>)}
                      </View>}
                      {selectedEngagement && <View style={simpleStyles.defaultsCard}>
                          <Text style={simpleStyles.sectionTitle}>{t('analysis.modal.reviewStandard')}</Text>
                          <Text>{t('analysis.modal.inheritedStandard', engagementVars)}</Text>
                          <Text style={simpleStyles.infoText}>{t('analysis.modal.reviewStandardHelp', engagementVars)}</Text>
                      </View>}

                      {(showAdvanced || selectedEngagement) && (
                          <View style={simpleStyles.advancedBlock}>
                              <AnalysisOptionsForm
                                  showAdvanced={showAdvanced}
                                  analysisMode={analysisMode}
                                  onAnalysisModeChange={setAnalysisMode}
                                  requestedLevel={requestedLevel}
                                  onRequestedLevelChange={setRequestedLevel}
                                  includeMarkerTable={includeMarkerTable}
                                  onIncludeMarkerTableChange={setIncludeMarkerTable}
                                  customPrompt={customPrompt}
                                  onCustomPromptChange={setCustomPrompt}
                                  language={language}
                                  onLanguageChange={setLanguage}
                                  includeInIcfProgress={includeInIcfProgress}
                                  onIncludeInIcfProgressChange={setIncludeInIcfProgress}
                                  tierAccess={tierAccess}
                                  showIcfProgressToggle={tierAccess.allowLevelSelect && !selectedEngagement}
                                  showLevelSection={tierAccess.allowLevelSelect}
                                  isTier3={isTier3}
                                  engagementOptions={engagementPreset ? [engagementPreset] : engagementOptions}
                                  selectedEngagementId={selectedEngagement?.id ?? null}
                                  onSelectedEngagementChange={engagementPreset ? undefined : handleEngagementChange}
                                  onUpgradePress={handleUpgradePress}
                                  disabled={isLoading}
                              />
                          </View>
                      )}

                      <Text style={{ marginTop: 14 }}>{t(summary.resultKey, engagementVars)}</Text>
                      {remainingCredits !== null && <Text style={{ marginTop: 8 }}>{t('analysis.simple.credits', { remaining: remainingCredits })}</Text>}
                      </ScrollView>

                      <View style={simpleStyles.footer}>
                          <TouchableOpacity
                              onPress={() => setShowAdvanced((value) => !value)}
                              style={simpleStyles.advancedLink}
                              disabled={isLoading}
                          >
                              <Text style={simpleStyles.advancedLinkText}>
                                  {showAdvanced
                                      ? t('analysis.simple.hideSettings')
                                      : t(selectedEngagement ? 'analysis.modal.additionalOptions' : 'analysis.simple.changeSettings')}
                              </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                              style={[simpleStyles.primaryAction, !canSubmit() && { opacity: 0.55 }]}
                              onPress={handleSubmit}
                              disabled={!canSubmit()}
                          >
                              {isLoading ? (
                                  <ActivityIndicator size="small" color="#FFFFFF" />
                              ) : (
                                  <>
                                      <Text style={simpleStyles.primaryActionText}>
                                          {t(selectedEngagement ? 'analysis.modal.prepareReview' : 'analysis.simple.start')}
                                      </Text>
                                      <MaterialIcons name="arrow-forward" size={20} color="#FFFFFF" />
                                  </>
                              )}
                          </TouchableOpacity>
                      </View>

                      {isLoading && (
                          <View style={simpleStyles.progressOverlay}>
                              <View style={simpleStyles.progressCard}>
                                  <ActivityIndicator size="large" color={reviewDesk.accent} />
                                  <Text style={simpleStyles.progressTitle}>
                                      {t('analysis.progress.title') || 'Analyzing Your Session'}
                                  </Text>
                                  <Text style={simpleStyles.progressCopy}>
                                      {t(selectedEngagement ? 'analysis.modal.queueingReview' : waitingTipKey)}
                                  </Text>
                              </View>
                          </View>
                      )}
                  </View>
              </View>
          </Modal>
          {upgradeModalEl}
          </>
      );
}

export default AnalysisModal;

const simpleStyles = StyleSheet.create({
    backdrop: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(21, 26, 35, 0.28)',
        padding: 24,
    },
    sheet: {
        width: 'min(640px, 96vw)' as any,
        maxHeight: '92vh' as any,
        overflow: 'hidden',
        borderRadius: 18,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
        boxShadow: reviewDesk.shadow,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
        paddingBottom: 18,
        borderBottomWidth: 1,
        borderBottomColor: reviewDesk.line,
    },
    closeButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: '#FFFDF9',
    },
    headerTitle: {
        color: reviewDesk.ink,
        fontSize: 24,
        fontWeight: '800',
        letterSpacing: -0.2,
    },
    headerSubtitle: {
        color: reviewDesk.inkSoft,
        fontSize: 14,
        lineHeight: 20,
        marginTop: 4,
    },
    fileCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: '#FFFDF9',
        padding: 14,
        marginTop: 18,
    },
    fileIcon: {
        width: 48,
        height: 48,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: reviewDesk.accentSoft,
    },
    fileName: {
        color: reviewDesk.ink,
        fontSize: 15,
        fontWeight: '800',
    },
    fileMeta: {
        color: reviewDesk.muted,
        fontSize: 13,
        marginTop: 3,
    },
    readyPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        borderRadius: 999,
        backgroundColor: reviewDesk.successSoft,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    readyPillText: {
        color: reviewDesk.accent,
        fontSize: 12,
        fontWeight: '800',
    },
    defaultsCard: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
        padding: 16,
        marginTop: 14,
        gap: 12,
    },
    pathCard: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.lineStrong,
        backgroundColor: reviewDesk.accentWash,
        padding: 16,
        marginTop: 14,
        gap: 12,
    },
    sectionTitle: {
        color: reviewDesk.ink,
        fontSize: 15,
        fontWeight: '800',
    },
    defaultRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    defaultText: {
        color: reviewDesk.inkSoft,
        fontSize: 14,
        fontWeight: '600',
    },
    pathRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    stepCircle: {
        width: 26,
        height: 26,
        borderRadius: 13,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: reviewDesk.accent,
    },
    stepCircleText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '800',
    },
    advancedBlock: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: '#FFFDF9',
        padding: 16,
        marginTop: 14,
    },
    infoNote: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        borderRadius: 10,
        backgroundColor: reviewDesk.amberSoft,
        padding: 13,
        marginTop: 14,
    },
    infoText: {
        flex: 1,
        color: reviewDesk.inkSoft,
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '600',
    },
    footer: {
        padding: 16,
        backgroundColor: reviewDesk.card,
        flexShrink: 0,
        flexWrap: 'wrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginTop: 18,
    },
    advancedLink: {
        paddingHorizontal: 6,
        paddingVertical: 10,
    },
    advancedLinkText: {
        color: reviewDesk.accent,
        fontSize: 14,
        fontWeight: '800',
    },
    primaryAction: {
        minHeight: 48,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        borderRadius: 9,
        backgroundColor: reviewDesk.accent,
        paddingHorizontal: 22,
        boxShadow: '0 10px 22px rgba(30, 64, 175, 0.18)',
    } as any,
    primaryActionText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
    progressOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 18,
        backgroundColor: 'rgba(255, 253, 248, 0.94)',
    },
    progressCard: {
        width: 320,
        alignItems: 'center',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
        padding: 24,
    },
    progressTitle: {
        color: reviewDesk.ink,
        fontSize: 17,
        fontWeight: '800',
        marginTop: 14,
        textAlign: 'center',
    },
    progressCopy: {
        color: reviewDesk.inkSoft,
        fontSize: 13,
        lineHeight: 18,
        marginTop: 8,
        textAlign: 'center',
    },
});
